import { Injectable } from '@angular/core';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { IHistoryOrderData } from '@df/session/api/history/history-order';
import { Nexus } from '@tomandco/nexus-api-types';

export namespace Order {
    export enum ENDPOINTS {
        cancel = 'order/cancel'
    }
    export enum EVENTS {
        Canceled = 'order.canceled'
    }
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            /**
             * time for order cancelation since order placement, given in minutes
             */
            order_cancellation_interval?: number;
        }
    }
}

export interface IResponseOrderCancel {
    customer: { order: IHistoryOrderData };
    success: Nexus.Api.Checkout.Success.Response['success'];
}

@Injectable({
    providedIn: 'root'
})
export class OrderService {
    constructor(
        private requestService: RequestService,
        private app: App,
        private events: Events
    ) {}

    /**
     *  cancels order with the given incremental Id
     */
    cancel(
        status: Status | undefined,
        incrementId: number | string,
        customerEmail: string,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseOrderCancel> {
        const request = this.requestService.send(
            status,
            {
                path: Order.ENDPOINTS.cancel,
                data: {
                    incrementId,
                    customerEmail
                }
            },
            onSuccess,
            onError
        );
        request.then(() => {
            this.events.broadcast(Order.EVENTS.Canceled);
        });
        return request;
    }

    /**
     * @param orderDate - date of order placement
     * @returns date til the order can be canceled by customer. Returns false if order cannot be canceled.
     */
    canCancelByDate(orderDate: string | Date): Date | false {
        orderDate = new Date(orderDate);
        const canCancelByDate = new Date(orderDate.getTime() + (this.orderCancellationInterval || 0) * 60000);
        const currentDate = new Date();
        return canCancelByDate && orderDate && canCancelByDate > currentDate ? canCancelByDate : false;
    }

    get orderCancellationInterval(): number | undefined {
        return this.app.data?.order_cancellation_interval;
    }
}
